import type { Mode } from "src/utils/types/mode";
import { formatIsoDate, numNightsBetweenTwoDates } from "src/utils/dateTime";
import type { InterestData, SearchResponse } from "src/api/SearchResponse";
import {
  type SupportedLanguageCode,
  isSupportedLanguageCode,
} from "src/utils/language";
import type { SchedulesResponse } from "src/api/SchedulesResponse";
import type { RoomDetail } from "src/domain/HotelsScreen/HotelsContext";
import { sendTravelAudienceEvent } from "./travelAudience/sendTravelAudienceEvent";
import type { DataPartners } from "./useDataPartner";
import { sendAdaraEvent } from "./adara/sendAdaraEvent";
import { getTransportData } from "./getTransportData";
import { sendStackAdaptPixel } from "./stackAdapt/sendStackAdaptPixel";
import { TICKET_SELECTION_LIVE_SCHEDULE_EXIT } from "./utils";

type TicketExitParams = {
  transitMode: Mode;
  language: string;
  searchResponse?: SearchResponse;
  routeIndex?: number;
  schedulesResponse?: SchedulesResponse;
  price?: string;
  origin?: string;
  destination?: string;
  originState?: string;
  destinationState?: string;
  departureDate?: string;
  returnDate?: string;
  numTripLegs?: number;
  carriers: string[];
  currency?: string;
  passengersCount?: number;
  interestData?: InterestData;
  isAdaraEnabled?: boolean;
};

type CarHireExitParams = {
  origin?: string;
  destination?: string;
  isAdaraEnabled?: boolean;
  languageCode?: SupportedLanguageCode;
};

type HotelExitParams = {
  destination?: string;
  destinationState?: string;
  hotelName?: string;
  hotelStarRating?: number;
  checkinDate?: Date;
  checkoutDate?: Date;
  languageCode?: SupportedLanguageCode;
  searchResponse?: SearchResponse;
  avgDailyRate?: number;
  roomDetails?: RoomDetail[];
  isAdaraEnabled: boolean;
};

export function logDataPartnerCarHireExit({
  origin,
  destination,
  languageCode,
  isAdaraEnabled,
}: CarHireExitParams) {
  if (isAdaraEnabled) {
    sendAdaraEvent({
      event: "carHireExit",
      departureCity: origin ?? "",
      arrivalCity: destination,
      siteLanguage: languageCode,
    });
  }

  sendStackAdaptPixel({
    event: "carHireExit",
    origin,
    destination,
  });
}

export function logDataPartnerHotelExit({
  destination,
  destinationState,
  hotelName,
  hotelStarRating,
  checkinDate,
  checkoutDate,
  languageCode,
  searchResponse,
  avgDailyRate,
  roomDetails,
  isAdaraEnabled,
}: HotelExitParams) {
  const formattedCheckIn = checkinDate && formatIsoDate(checkinDate);
  const formattedCheckOut = checkoutDate && formatIsoDate(checkoutDate);

  sendStackAdaptPixel({
    event: "hotelExit",
    destination,
    hotelName,
    checkinDate,
    checkoutDate,
  });

  if (isAdaraEnabled) {
    const arrivalCountry =
      searchResponse?.places[searchResponse.result.destinationPlace]
        ?.countryCode;
    const numNights =
      checkinDate &&
      checkoutDate &&
      numNightsBetweenTwoDates(checkinDate, checkoutDate);

    const { numAdults, numChildren, numRooms } =
      roomDetails?.reduce(
        (acc, room) => {
          return {
            ...acc,
            numAdults: acc.numAdults + room.adults,
            numChildren: acc.numChildren + room.children.length,
          };
        },
        { numAdults: 0, numChildren: 0, numRooms: roomDetails.length },
      ) ?? {};

    sendAdaraEvent({
      event: "hotelExit",
      arrivalCity: destination,
      hotelName,
      hotelCheckInDate: formattedCheckIn,
      hotelCheckOutDate: formattedCheckOut,
      siteLanguage: languageCode,
      numNights,
      avgDailyRate,
      currencyType: searchResponse?.request.currencyCode,
      hotelStarRating,
      arrivalState: destinationState,
      arrivalCountry,
      numAdults,
      numChildren,
      numRooms,
    });
  }
}

export function logDataPartnerTicketExit({
  transitMode,
  language,
  searchResponse,
  routeIndex,
  schedulesResponse,
  price,
  origin,
  destination,
  originState,
  destinationState,
  departureDate,
  returnDate,
  numTripLegs,
  carriers,
  currency,
  passengersCount,
  interestData,
  isAdaraEnabled,
}: TicketExitParams) {
  const enabledPartners: DataPartners = Object.keys(interestData ?? {}).filter(
    (key) => interestData?.[key].enabled,
  ) as DataPartners;

  if (isAdaraEnabled) {
    const data = getTransportData({
      searchResponse,
      routeIndex,
      schedulesResponse,
      transitMode,
      numTripLegs,
      returnDate,
    });

    // Strip out currency and commas from round trip prices.
    const formattedPrice = price?.replace(/[^\d.-]+/g, "");

    sendAdaraEvent({
      event: "transportExit",
      travelProductType: transitMode,
      departureDate: departureDate ?? data.departureDate,
      departureCity: origin ?? "",
      departureState: originState,
      departureCountry: data.departureCountry,
      departureAirportCode: data.departureAirportCode,
      arrivalDate: returnDate,
      arrivalCity: destination,
      arrivalState: destinationState,
      arrivalCountry: data.arrivalCountry,
      arrivalAirportCode: data.arrivalAirportCode,
      ticketCarriers: carriers,
      numPassengers: passengersCount,
      tripType: data.tripType,
      price: formattedPrice ?? data.price,
      currencyType: currency ?? data.currencyType,
      ticketClass: data.ticketClass,
      siteLanguage: isSupportedLanguageCode(language) ? language : undefined,
    });
  }

  if (enabledPartners.includes("TravelAudience")) {
    sendTravelAudienceEvent({
      level: TICKET_SELECTION_LIVE_SCHEDULE_EXIT,
      transitMode,
      language,
      price,
      origin,
      destination,
      departureDate,
      returnDate,
      operator: carriers.join(", "),
      currency,
    });
  }

  sendStackAdaptPixel({
    event: "transportExit",
    origin,
    destination,
    travelMode: transitMode,
    outBoundDate: departureDate,
    inBoundDate: returnDate,
    passengerCount: passengersCount,
    ticketCarriers: carriers,
    totalPrice: price,
  });
}
